import React, { useState, useEffect } from 'react';
import { Award, Calendar, AlertTriangle, Filter, Download, Upload } from 'lucide-react';
import { GlassCard } from '../components/GlassCard';
import { Button } from '../components/Button';
import { Badge } from '../components/Badge';
import apiService, { Qualification } from '../services/api';

interface TypeRating {
  id: string;
  crewMember: string;
  aircraft: string;
  issueDate: string;
  expiryDate: string;
  status: 'valid' | 'expiring-soon' | 'expired';
  daysUntilExpiry: number;
}

interface RouteQualification {
  id: string;
  crewMember: string;
  route: string;
  airport: string;
  type: 'departure' | 'arrival' | 'both';
  issueDate: string;
  expiryDate: string;
  status: 'valid' | 'expiring-soon' | 'expired';
}

interface TrainingDue {
  id: string;
  crewMember: string;
  course: string;
  category: 'recurrent' | 'initial' | 'upgrade';
  dueDate: string;
  status: 'upcoming' | 'overdue' | 'completed';
}

export const Qualifications: React.FC = () => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [qualifications, setQualifications] = useState<Qualification[]>([]);
  const [typeRatings, setTypeRatings] = useState<TypeRating[]>([]);
  const [routeQuals, setRouteQuals] = useState<RouteQualification[]>([]);
  const [trainingDue, setTrainingDue] = useState<TrainingDue[]>([]);
  const [selectedFilter, setSelectedFilter] = useState<string>('all');
  const [searchQuery, setSearchQuery] = useState('');

  useEffect(() => {
    const fetchQualificationsData = async () => {
      setLoading(true);
      setError(null);

      try {
        // Fetch real qualifications from API
        const data = await apiService.getQualifications();
        setQualifications(data);

        // Transform API data into type ratings format
        const typeRatingsData: TypeRating[] = data
          .filter(q => q.qualification_type === 'Type Rating' || q.aircraft_type)
          .map(q => {
            const expiryDate = new Date(q.expiry_date);
            const today = new Date();
            const daysUntilExpiry = Math.floor((expiryDate.getTime() - today.getTime()) / (1000 * 60 * 60 * 24));

            let status: 'valid' | 'expiring-soon' | 'expired' = 'valid';
            if (daysUntilExpiry < 0) status = 'expired';
            else if (daysUntilExpiry < 30) status = 'expiring-soon';

            return {
              id: String(q.id),
              crewMember: `Crew Member #${q.crew_member_id}`,
              aircraft: q.aircraft_type || 'Unknown',
              issueDate: q.issue_date,
              expiryDate: q.expiry_date,
              status,
              daysUntilExpiry,
            };
          });

        setTypeRatings(typeRatingsData);

        // Transform route qualifications
        const routeQualsData: RouteQualification[] = data
          .filter(q => q.qualification_type === 'Route Qualification')
          .map(q => {
            const expiryDate = new Date(q.expiry_date);
            const today = new Date();
            const daysUntilExpiry = Math.floor((expiryDate.getTime() - today.getTime()) / (1000 * 60 * 60 * 24));

            let status: 'valid' | 'expiring-soon' | 'expired' = 'valid';
            if (daysUntilExpiry < 0) status = 'expired';
            else if (daysUntilExpiry < 30) status = 'expiring-soon';

            return {
              id: String(q.id),
              crewMember: `Crew Member #${q.crew_member_id}`,
              route: q.certificate_number || 'N/A',
              airport: q.notes || 'N/A',
              type: 'both' as const,
              issueDate: q.issue_date,
              expiryDate: q.expiry_date,
              status,
            };
          });

        setRouteQuals(routeQualsData);

        // Transform training qualifications
        const trainingData: TrainingDue[] = data
          .filter(q => q.qualification_type === 'Training')
          .map(q => {
            const expiryDate = new Date(q.expiry_date);
            const today = new Date();
            const daysUntilExpiry = Math.floor((expiryDate.getTime() - today.getTime()) / (1000 * 60 * 60 * 24));

            let status: 'upcoming' | 'overdue' | 'completed' = 'upcoming';
            if (daysUntilExpiry < 0) status = 'overdue';
            else if (q.status === 'completed') status = 'completed';

            return {
              id: String(q.id),
              crewMember: `Crew Member #${q.crew_member_id}`,
              course: q.aircraft_type || q.notes || 'Training',
              category: 'recurrent' as const,
              dueDate: q.expiry_date,
              status,
            };
          });

        setTrainingDue(trainingData);

      } catch (err: any) {
        console.error('Failed to fetch qualifications data:', err);
        setError(err.message || 'Failed to load qualifications');

        // Fallback to empty arrays - no dummy data
        setTypeRatings([]);
        setRouteQuals([]);
        setTrainingDue([]);
      } finally {
        setLoading(false);
      }
    };

    fetchQualificationsData();
  }, []);

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'valid':
        return <Badge variant="success">Valid</Badge>;
      case 'expiring-soon':
        return <Badge variant="warning">Expiring Soon</Badge>;
      case 'expired':
        return <Badge variant="danger">Expired</Badge>;
      case 'upcoming':
        return <Badge variant="info">Upcoming</Badge>;
      case 'overdue':
        return <Badge variant="danger">Overdue</Badge>;
      case 'completed':
        return <Badge variant="success">Completed</Badge>;
      default:
        return <Badge variant="secondary">{status}</Badge>;
    }
  };

  const filteredTypeRatings = typeRatings.filter((rating) => {
    const matchesSearch =
      rating.crewMember.toLowerCase().includes(searchQuery.toLowerCase()) ||
      rating.aircraft.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesFilter = selectedFilter === 'all' || rating.status === selectedFilter;
    return matchesSearch && matchesFilter;
  });

  if (loading) {
    return (
      <div className="p-6 space-y-6 animate-pulse">
        <div className="h-32 bg-white/5 rounded-xl"></div>
        <div className="h-96 bg-white/5 rounded-xl"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-6">
        <GlassCard className="p-6 text-center">
          <AlertTriangle className="w-12 h-12 text-red-400 mx-auto mb-4" />
          <h2 className="text-xl font-semibold text-white mb-2">Error Loading Qualifications</h2>
          <p className="text-white/60 mb-4">{error}</p>
          <Button onClick={() => window.location.reload()}>Retry</Button>
        </GlassCard>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <GlassCard className="p-6">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold text-white mb-2">Qualifications Management</h1>
            <p className="text-white/60">Track and manage crew qualifications and currency</p>
          </div>
          <div className="flex items-center gap-2">
            <Button
              variant="secondary"
              className="flex items-center gap-2"
              onClick={() => {
                if (qualifications.length > 0) {
                  const blob = new Blob([JSON.stringify(qualifications, null, 2)], { type: 'application/json' });
                  const url = URL.createObjectURL(blob);
                  const a = document.createElement('a');
                  a.href = url;
                  a.download = `qualifications-${new Date().toISOString().split('T')[0]}.json`;
                  a.click();
                  URL.revokeObjectURL(url);
                }
              }}
              disabled={qualifications.length === 0}
            >
              <Download className="w-4 h-4" />
              Export
            </Button>
            <Button
              variant="primary"
              className="flex items-center gap-2"
              onClick={() => alert('Bulk update functionality coming soon')}
            >
              <Upload className="w-4 h-4" />
              Bulk Update
            </Button>
          </div>
        </div>
      </GlassCard>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
        <GlassCard className="p-6">
          <div className="flex items-center justify-between mb-2">
            <Award className="w-8 h-8 text-green-400" />
            <Badge variant="success">{typeRatings.filter((r) => r.status === 'valid').length}</Badge>
          </div>
          <h3 className="text-white/60 text-sm">Valid Ratings</h3>
          <p className="text-2xl font-bold text-white mt-1">{typeRatings.filter((r) => r.status === 'valid').length}</p>
        </GlassCard>

        <GlassCard className="p-6">
          <div className="flex items-center justify-between mb-2">
            <AlertTriangle className="w-8 h-8 text-yellow-400" />
            <Badge variant="warning">{typeRatings.filter((r) => r.status === 'expiring-soon').length}</Badge>
          </div>
          <h3 className="text-white/60 text-sm">Expiring Soon</h3>
          <p className="text-2xl font-bold text-white mt-1">{typeRatings.filter((r) => r.status === 'expiring-soon').length}</p>
        </GlassCard>

        <GlassCard className="p-6">
          <div className="flex items-center justify-between mb-2">
            <AlertTriangle className="w-8 h-8 text-red-400" />
            <Badge variant="danger">{typeRatings.filter((r) => r.status === 'expired').length}</Badge>
          </div>
          <h3 className="text-white/60 text-sm">Expired</h3>
          <p className="text-2xl font-bold text-white mt-1">{typeRatings.filter((r) => r.status === 'expired').length}</p>
        </GlassCard>

        <GlassCard className="p-6">
          <div className="flex items-center justify-between mb-2">
            <Calendar className="w-8 h-8 text-sky-400" />
            <Badge variant="info">{trainingDue.filter((t) => t.status === 'upcoming').length}</Badge>
          </div>
          <h3 className="text-white/60 text-sm">Training Due</h3>
          <p className="text-2xl font-bold text-white mt-1">{trainingDue.filter((t) => t.status === 'upcoming').length}</p>
        </GlassCard>
      </div>

      {/* Filters */}
      <GlassCard className="p-6">
        <div className="flex flex-col md:flex-row gap-4">
          <div className="flex-1 relative">
            <input
              type="text"
              placeholder="Search crew members or aircraft..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-4 pr-4 py-2 bg-white/5 border border-white/10 rounded-lg text-white placeholder-white/40 focus:outline-none focus:border-sky-500 transition-colors"
            />
          </div>
          <div className="relative">
            <Filter className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-white/40" />
            <select
              value={selectedFilter}
              onChange={(e) => setSelectedFilter(e.target.value)}
              className="pl-10 pr-4 py-2 bg-white/5 border border-white/10 rounded-lg text-white appearance-none focus:outline-none focus:border-sky-500 transition-colors"
            >
              <option value="all">All Statuses</option>
              <option value="valid">Valid</option>
              <option value="expiring-soon">Expiring Soon</option>
              <option value="expired">Expired</option>
            </select>
          </div>
        </div>
      </GlassCard>

      {/* Type Ratings Table */}
      <GlassCard className="p-6">
        <div className="flex items-center gap-2 mb-4">
          <Award className="w-5 h-5 text-sky-400" />
          <h2 className="text-xl font-semibold text-white">Type Ratings</h2>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b border-white/10">
                <th className="text-left py-3 px-4 text-white/80 font-medium">Crew Member</th>
                <th className="text-left py-3 px-4 text-white/80 font-medium">Aircraft</th>
                <th className="text-left py-3 px-4 text-white/80 font-medium">Issue Date</th>
                <th className="text-left py-3 px-4 text-white/80 font-medium">Expiry Date</th>
                <th className="text-left py-3 px-4 text-white/80 font-medium">Days Until Expiry</th>
                <th className="text-left py-3 px-4 text-white/80 font-medium">Status</th>
              </tr>
            </thead>
            <tbody>
              {filteredTypeRatings.map((rating) => (
                <tr key={rating.id} className="border-b border-white/5 hover:bg-white/5 transition-colors">
                  <td className="py-3 px-4 text-white">{rating.crewMember}</td>
                  <td className="py-3 px-4 text-white font-medium">{rating.aircraft}</td>
                  <td className="py-3 px-4 text-white/80">{rating.issueDate}</td>
                  <td className="py-3 px-4 text-white/80">{rating.expiryDate}</td>
                  <td className="py-3 px-4">
                    <span
                      className={`font-medium ${
                        rating.daysUntilExpiry < 0
                          ? 'text-red-400'
                          : rating.daysUntilExpiry < 30
                          ? 'text-yellow-400'
                          : 'text-green-400'
                      }`}
                    >
                      {rating.daysUntilExpiry < 0 ? 'Expired' : `${rating.daysUntilExpiry} days`}
                    </span>
                  </td>
                  <td className="py-3 px-4">{getStatusBadge(rating.status)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </GlassCard>

      {/* Route Qualifications */}
      <GlassCard className="p-6">
        <div className="flex items-center gap-2 mb-4">
          <Calendar className="w-5 h-5 text-sky-400" />
          <h2 className="text-xl font-semibold text-white">Route Qualifications</h2>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b border-white/10">
                <th className="text-left py-3 px-4 text-white/80 font-medium">Crew Member</th>
                <th className="text-left py-3 px-4 text-white/80 font-medium">Airport</th>
                <th className="text-left py-3 px-4 text-white/80 font-medium">ICAO Code</th>
                <th className="text-left py-3 px-4 text-white/80 font-medium">Type</th>
                <th className="text-left py-3 px-4 text-white/80 font-medium">Issue Date</th>
                <th className="text-left py-3 px-4 text-white/80 font-medium">Expiry Date</th>
                <th className="text-left py-3 px-4 text-white/80 font-medium">Status</th>
              </tr>
            </thead>
            <tbody>
              {routeQuals.map((qual) => (
                <tr key={qual.id} className="border-b border-white/5 hover:bg-white/5 transition-colors">
                  <td className="py-3 px-4 text-white">{qual.crewMember}</td>
                  <td className="py-3 px-4 text-white/80">{qual.airport}</td>
                  <td className="py-3 px-4 text-white font-medium">{qual.route}</td>
                  <td className="py-3 px-4">
                    <Badge variant="secondary">{qual.type}</Badge>
                  </td>
                  <td className="py-3 px-4 text-white/80">{qual.issueDate}</td>
                  <td className="py-3 px-4 text-white/80">{qual.expiryDate}</td>
                  <td className="py-3 px-4">{getStatusBadge(qual.status)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </GlassCard>

      {/* Training Due */}
      <GlassCard className="p-6">
        <div className="flex items-center gap-2 mb-4">
          <AlertTriangle className="w-5 h-5 text-sky-400" />
          <h2 className="text-xl font-semibold text-white">Training Due Dates</h2>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b border-white/10">
                <th className="text-left py-3 px-4 text-white/80 font-medium">Crew Member</th>
                <th className="text-left py-3 px-4 text-white/80 font-medium">Course</th>
                <th className="text-left py-3 px-4 text-white/80 font-medium">Category</th>
                <th className="text-left py-3 px-4 text-white/80 font-medium">Due Date</th>
                <th className="text-left py-3 px-4 text-white/80 font-medium">Status</th>
              </tr>
            </thead>
            <tbody>
              {trainingDue.map((training) => (
                <tr key={training.id} className="border-b border-white/5 hover:bg-white/5 transition-colors">
                  <td className="py-3 px-4 text-white">{training.crewMember}</td>
                  <td className="py-3 px-4 text-white font-medium">{training.course}</td>
                  <td className="py-3 px-4">
                    <Badge variant="secondary">{training.category}</Badge>
                  </td>
                  <td className="py-3 px-4 text-white/80">{training.dueDate}</td>
                  <td className="py-3 px-4">{getStatusBadge(training.status)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </GlassCard>
    </div>
  );
};

export default Qualifications;
